/**
 * COMPANY DOCUMENT UPLOAD COMPONENT
 * 
 * Allows users to upload documents for company KYC verification.
 * Supports multiple document types with file validation.
 */

import React, { useState, useRef } from 'react';
import { getSessionHeaders } from '../../lib/serverSession'
import type { CompanyDocumentType } from '../../types/company';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface CompanyDocumentUploadProps {
  companyId: string;
  userId: number;
  onUploadComplete: () => void;
  onClose: () => void;
}

const documentTypeOptions: { value: CompanyDocumentType; label: string; description: string }[] = [
  { value: 'certificate_of_incorporation', label: 'Certificate of Incorporation', description: 'Official company registration certificate' },
  { value: 'memorandum_articles', label: 'Memorandum & Articles', description: 'MOI or company constitution' },
  { value: 'register_of_directors', label: 'Register of Directors', description: 'Official director registry document' },
  { value: 'register_of_shareholders', label: 'Register of Shareholders', description: 'Official shareholder registry' },
  { value: 'proof_of_address', label: 'Proof of Address', description: 'Utility bill or bank statement showing company address' },
  { value: 'tax_clearance', label: 'Tax Clearance Certificate', description: 'SARS tax compliance certificate' },
  { value: 'business_license', label: 'Business License', description: 'Operating license if applicable' },
  { value: 'ubo_declaration', label: 'UBO Declaration', description: 'Ultimate Beneficial Owner declaration' },
  { value: 'bank_statement', label: 'Bank Statement', description: 'Recent company bank statement' },
  { value: 'resolution', label: 'Board Resolution', description: 'Signed board resolution' },
  { value: 'power_of_attorney', label: 'Power of Attorney', description: 'Authorization document' },
  { value: 'other', label: 'Other Document', description: 'Other supporting document' },
];

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const CompanyDocumentUpload: React.FC<CompanyDocumentUploadProps> = ({
  companyId, userId, onUploadComplete, onClose
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<CompanyDocumentType>('certificate_of_incorporation');
  const [documentName, setDocumentName] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Invalid file type. Please upload PDF, JPG, or PNG files only.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('File too large. Maximum size is 10MB.');
      return;
    }
    setSelectedFile(file);
    setError(null);
    if (!documentName) setDocumentName(file.name.replace(/\.[^/.]+$/, ''));
  };

  const handleUpload = async () => {
    if (!selectedFile) { setError('Please select a file'); return; }

    setUploading(true);
    setError(null);

    try {
      // Convert file to base64
      const reader = new FileReader();
      const fileBase64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(selectedFile);
      });

      const response = await fetch(`${API_BASE_URL}/api/user/company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'upload_company_document',
          data: {
            companyId,
            documentType,
            documentName: documentName || selectedFile.name,
            description,
            fileBase64,
            fileName: selectedFile.name,
            fileType: selectedFile.type,
            fileSize: selectedFile.size
          }
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      alert('Document uploaded successfully!');
      onUploadComplete();
    } catch (err) {
      console.error('Upload error:', err);
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-900 rounded-xl max-w-lg w-full border border-gray-700 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h3 className="text-xl font-bold text-white">📄 Upload Company Document</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">×</button>
        </div>
        <div className="p-6 space-y-4">
          {/* Document Type */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Document Type *</label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value as CompanyDocumentType)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
            >
              {documentTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <p className="text-sm text-gray-500 mt-1">{documentTypeOptions.find(o => o.value === documentType)?.description}</p>
          </div>

          {/* Document Name */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Document Name *</label>
            <input
              type="text"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
              placeholder="Enter document name"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Description (Optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white resize-none focus:border-yellow-500 focus:outline-none"
              rows={3}
              placeholder="Add notes about this document..."
            />
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Select File *</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full px-4 py-8 border-2 border-dashed border-gray-600 rounded-lg hover:border-yellow-500 transition-colors cursor-pointer"
            >
              {selectedFile ? (
                <div className="text-center">
                  <span className="text-3xl mb-2 block">📎</span>
                  <p className="text-white font-medium">{selectedFile.name}</p>
                  <p className="text-sm text-white">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  <p className="text-sm text-yellow-400 mt-2">Click to change file</p>
                </div>
              ) : (
                <div className="text-center text-white">
                  <span className="text-3xl mb-2 block">📁</span>
                  <p>Click to select file</p>
                  <p className="text-sm mt-1">PDF, JPG, PNG (max 10MB)</p>
                </div>
              )}
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="p-3 bg-red-900/30 border border-red-500 rounded-lg text-red-400 text-sm">
              ⚠️ {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={uploading}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="flex-1 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin">⏳</span> Uploading...
                </span>
              ) : (
                '📤 Upload Document'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDocumentUpload;
